import React, { useState, useEffect } from 'react';
import { X, Plus, Users, Phone, Clock, Bell, CheckCircle, Trash2, UserPlus, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { P } from '@/config/permissionCodes';
import { useAuth } from '@/context/AuthContext';

export default function WaitingListModal({ waitingList = [], onAdd, onNotify, onRemove, onSeated, onClose, availableTablesCount = 0 }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    pax: 2,
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const { hasPermission } = useAuth();

    const canWaitlistAdd    = hasPermission(P.POS.DINE_IN.WAITLIST.ADD);
    const canWaitlistRemove = hasPermission(P.POS.DINE_IN.WAITLIST.REMOVE);
    const canWaitlistNotify = hasPermission(P.POS.DINE_IN.WAITLIST.NOTIFY);
    const canAccessWaitlist = canWaitlistAdd || canWaitlistRemove || canWaitlistNotify;

  // Auto-refresh wait times every minute
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-\s]{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Enter a valid phone number';
    }
    if (formData.pax < 1 || formData.pax > 20) {
      newErrors.pax = 'Party size must be 1-20';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddCustomer = () => {
    if (!validateForm()) return;

    onAdd({
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      pax: formData.pax,
      notes: formData.notes.trim()
    });

    // Reset form
    setFormData({
      name: '',
      phone: '',
      pax: 2,
      notes: ''
    });
    setErrors({});
    setShowAddForm(false);
    toast.success('Customer added to waiting list');
  };

  const activeWaiting = (waitingList || []).filter(w => w.status === 'WAITING');

  const formatWaitTime = (addedAt) => {
    const diff = new Date() - new Date(addedAt);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
  };

  const getWaitTimeColor = (addedAt) => {
    const diff = new Date() - new Date(addedAt);
    const mins = Math.floor(diff / 60000);
    if (mins < 15) return 'text-teal-600';
    if (mins < 30) return 'text-amber-600';
    return 'text-red-600';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && showAddForm) {
      handleAddCustomer();
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header - Teal gradient */}
        <div className="bg-teal-600 text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Users size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Waiting List</h2>
                <p className="text-sm text-teal-100 mt-0.5">
                  {activeWaiting.length} customer{activeWaiting.length !== 1 ? 's' : ''} in queue
                  {availableTablesCount > 0 && (
                    <span className="ml-2 text-emerald-200">
                      • {availableTablesCount} table{availableTablesCount !== 1 ? 's' : ''} available
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">

          {/* Add Customer Button */}
          {!showAddForm && canWaitlistAdd && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full p-4 mb-4 border-2 border-dashed border-teal-300 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all flex items-center justify-center gap-2 text-teal-700 font-bold"
            >
              <UserPlus size={20} />
              Add Customer to Queue
            </button>
          )}

          {/* Add Customer Form */}
          {showAddForm && (
            <div className="bg-white border-2 border-teal-200 rounded-xl p-6 mb-4 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <UserPlus size={20} className="text-teal-600" />
                <h3 className="font-bold text-slate-900">Add New Customer</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (errors.name) setErrors({ ...errors, name: null });
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="Customer name"
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm ${
                      errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-teal-500'
                    }`}
                    autoFocus
                  />
                  {errors.name && (
                    <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      setFormData({ ...formData, phone: e.target.value });
                      if (errors.phone) setErrors({ ...errors, phone: null });
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="+91 98765 43210"
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm ${
                      errors.phone ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-teal-500'
                    }`}
                  />
                  {errors.phone && (
                    <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.phone}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Party Size
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5, 6].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setFormData({ ...formData, pax: num })}
                        className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${
                          formData.pax === num
                            ? 'bg-teal-600 text-white shadow-md'
                            : 'bg-slate-100 text-slate-700 hover:bg-teal-100'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                    <input
                      type="number"
                      value={formData.pax > 6 ? formData.pax : ''}
                      onChange={(e) => setFormData({ ...formData, pax: parseInt(e.target.value) || 2 })}
                      placeholder="7+"
                      min="1"
                      max="20"
                      className="w-16 px-2 py-2.5 border-2 border-slate-200 rounded-lg focus:border-teal-500 focus:outline-none text-sm text-center font-bold"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Notes <span className="text-slate-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder="Special requests, preferences..."
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setErrors({});
                  }}
                  className="flex-1 px-4 py-2.5 bg-white text-slate-700 rounded-lg font-semibold hover:bg-slate-100 transition-all border-2 border-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCustomer}
                  className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-bold transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <Plus size={18} />
                  Add to Queue
                </button>
              </div>
            </div>
          )}

          {/* Waiting List */}
          {activeWaiting.length > 0 ? (
            <div className="space-y-3">
              {activeWaiting.map((customer, index) => (
                <div
                  key={customer.id}
                  className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between">
                    {/* Customer Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-11 h-11 bg-teal-500 rounded-full flex items-center justify-center font-black text-white shadow-md">
                          #{index + 1}
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-slate-900">{customer.name}</h4>
                          <div className="flex items-center gap-4 mt-1 flex-wrap">
                            <span className="text-sm text-slate-600 flex items-center gap-1.5">
                              <Phone size={14} className="text-slate-400" />
                              {customer.phone}
                            </span>
                            <span className="text-sm text-slate-600 flex items-center gap-1.5">
                              <Users size={14} className="text-slate-400" />
                              {customer.pax} guest{customer.pax !== 1 ? 's' : ''}
                            </span>
                            <span className={`text-sm font-bold flex items-center gap-1.5 ${getWaitTimeColor(customer.addedAt)}`}>
                              <Clock size={14} />
                              {formatWaitTime(customer.addedAt)}
                            </span>
                          </div>
                          {customer.notes && (
                            <p className="text-xs text-slate-500 mt-2 bg-slate-50 px-2 py-1 rounded inline-block">
                              {customer.notes}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Notification Status */}
                      {customer.notified && (
                        <div className="mt-3 inline-flex items-center gap-1.5 text-xs bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full font-semibold">
                          <CheckCircle size={14} />
                          Notified at {new Date(customer.notifiedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-4">
                      {canWaitlistNotify && (
                        !customer.notified ? (
                          <button
                            onClick={() => {
                              onNotify(customer.id);
                              toast.success(`Notification sent to ${customer.name}`);
                            }}
                            className="px-4 py-2 bg-teal-100 text-teal-700 rounded-lg font-semibold hover:bg-teal-200 transition-all flex items-center gap-2 text-sm"
                          >
                            <Bell size={16} />
                            Notify
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              onSeated(customer.id);
                              toast.success(`${customer.name} marked as seated`);
                            }}
                            className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg font-semibold hover:bg-emerald-200 transition-all flex items-center gap-2 text-sm"
                          >
                            <CheckCircle size={16} />
                            Seated
                          </button>
                        )
                      )}
                      {canWaitlistRemove && (
                      <button
                        onClick={() => {
                          onRemove(customer.id);
                          toast.success(`${customer.name} removed from queue`);
                        }}
                        className="px-4 py-2 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-all flex items-center gap-2 text-sm"
                      >
                        <Trash2 size={16} />
                        Remove
                      </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <Users size={36} className="text-teal-500" />
              </div>
              <p className="text-slate-700 font-semibold text-lg">No customers waiting</p>
              <p className="text-slate-500 text-sm mt-2 max-w-xs">
                Add customers to the queue when all tables are occupied
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 bg-white">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Tip: Click "Notify" to alert customers when their table is ready
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
